import { useRef } from 'react'

import { useBoundStore } from '../store/store'
import type { ApiResponse } from '../types/api'
import type { UserInfo } from '../types/user'
import { api } from '../utils/api-client'
import { getDeviceInfo } from '../utils/device'
import { syncFcmToken } from '../utils/fcmToken'
import { getPublicAuth } from '../utils/getPublicAuth'
import { encryptUser } from '../utils/user'

const genericError = 'No se pudo iniciar sesión. Inténtalo de nuevo.'

interface UsePasswordLoginOptions {
  // Login succeeded and the session is stored.
  onSuccess?: () => void
  // Login failed (bad credentials / network); the caller shows a toast.
  onError?: (message: string) => void
  // A status-2 redirect the backend hands back as a path string (rare).
  onRedirect?: (path: string) => void
}

// Logs a lobby iframe player in with username/password. The iframe can't sign
// the request itself — only the parent owns the correct API host + VITE_API_KEY
// — so it forwards the credentials and this runs the same flow the login modal
// does (center.php -> link.php -> store). Standalone by choice, reusing the
// low-level utils rather than touching the login modal, like useOAuthLogin.
export const usePasswordLogin = ({
  onSuccess,
  onError,
  onRedirect,
}: UsePasswordLoginOptions = {}) => {
  const setUserStore = useBoundStore(state => state.user.setUser)
  // Blocks a second submit while one is in flight (postMessage has no debounce).
  const busyRef = useRef(false)

  const startPasswordLogin = async (usernameRaw: string, password: string) => {
    const username = usernameRaw.toLowerCase().trim()
    if (!username || !password) {
      onError?.(genericError)
      return
    }
    if (busyRef.current) return
    busyRef.current = true

    try {
      const publicAuth = await getPublicAuth()
      const response = await api.post<ApiResponse<UserInfo | string>>(
        '/center.php',
        {
          username,
          password,
          submit_type: 'login',
          meta: getDeviceInfo(),
          auth: publicAuth,
        },
      )
      const { status, info } = response.data

      if (status === 1 && typeof info !== 'string') {
        const privateAuth = await api.post<
          ApiResponse<string | { auth: string; expired_time: number }>
        >('/link.php', {
          username: info.username,
          password,
          auth: publicAuth,
        })
        if (
          privateAuth.data.status !== 1 ||
          typeof privateAuth.data.info === 'string'
        ) {
          onError?.(
            typeof privateAuth.data.info === 'string'
              ? privateAuth.data.info
              : genericError,
          )
          return
        }
        localStorage.setItem('privateAuth', privateAuth.data.info.auth)
        encryptUser({ username: info.username, password })
        setUserStore(info)
        void syncFcmToken()
        onSuccess?.()
        return
      }

      // status 2 is a redirect the backend returns as a path string.
      if (status === 2 && typeof info === 'string') {
        onRedirect?.(info)
        return
      }

      onError?.(typeof info === 'string' ? info : genericError)
    } catch {
      onError?.(genericError)
    } finally {
      busyRef.current = false
    }
  }

  return { startPasswordLogin }
}
